import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { GeoLocationFilters } from "../../components/GeoLocationFilters";
import { useGeoDirectory } from "../../context/GeoDirectoryContext";
import { useAppState } from "../../context/AppStateContext";
import { useToast } from "../../context/ToastContext";
import type { MobilePayload, MobilePhotoFromDb } from "../../types";

function cloneMobile(m: MobilePayload): MobilePayload {
  return {
    ...m,
    documentsFromMobile: [...m.documentsFromMobile],
    photosFromMobile: m.photosFromMobile.map((p) => ({ ...p })),
  };
}

function isHttpUrlForImage(u: string): boolean {
  try {
    const x = new URL(u);
    return x.protocol === "http:" || x.protocol === "https:";
  } catch {
    return false;
  }
}

function sanitizePhotosForSave(photos: MobilePhotoFromDb[]): MobilePhotoFromDb[] {
  return photos
    .map((p) => ({
      label: String(p.label ?? "").trim() || String(p.url ?? "").trim() || "Photo",
      url: String(p.url ?? "").trim(),
    }))
    .filter((p) => p.url);
}

export function BeneficiaryTab() {
  const { id } = useParams<{ id: string }>();
  const { getById, updateApplication } = useAppState();
  const { showToast } = useToast();
  const { store: geoStore } = useGeoDirectory();
  const app = id ? getById(id) : undefined;

  const [mobileDraft, setMobileDraft] = useState<MobilePayload | null>(null);
  const [districtDraft, setDistrictDraft] = useState("");
  const [mandalDraft, setMandalDraft] = useState("");
  const [villageDraft, setVillageDraft] = useState("");

  useEffect(() => {
    if (!app) return;
    setMobileDraft(cloneMobile(app.mobile));
    const districtKey = geoStore?.findDistrict(app.district)?.districtKey ?? app.district;
    setDistrictDraft(districtKey);
    setMandalDraft(app.mandal);
    setVillageDraft(app.village);
  }, [app?.id, app?.district, app?.mandal, app?.village, geoStore]);

  const dirty = useMemo(() => {
    if (!app || !mobileDraft) return false;
    const m = mobileDraft;
    const o = app.mobile;
    const mobileChanged =
      m.beneficiaryName !== o.beneficiaryName ||
      m.fatherOrHusbandName !== o.fatherOrHusbandName ||
      m.aadhaarNumber !== o.aadhaarNumber ||
      m.applicationId !== o.applicationId ||
      m.houseSanctionNumber !== o.houseSanctionNumber ||
      m.bankName !== o.bankName ||
      m.bankAccountNumber !== o.bankAccountNumber ||
      m.ifscCode !== o.ifscCode;
    const geoChanged =
      districtDraft !== app.district || mandalDraft !== app.mandal || villageDraft !== app.village;
    return mobileChanged || geoChanged;
  }, [app, mobileDraft, districtDraft, mandalDraft, villageDraft]);

  const discard = useCallback(() => {
    if (!app) return;
    setMobileDraft(cloneMobile(app.mobile));
    setDistrictDraft(geoStore?.findDistrict(app.district)?.districtKey ?? app.district);
    setMandalDraft(app.mandal);
    setVillageDraft(app.village);
  }, [app, geoStore]);

  const save = useCallback(() => {
    if (!app || !mobileDraft) return;
    const districtRow = geoStore?.findDistrict(districtDraft);
    if (!districtRow) {
      showToast("Choose a valid district from the geo directory.", "error");
      return;
    }
    const cleanedPhotos = sanitizePhotosForSave(mobileDraft.photosFromMobile);
    void (async () => {
      try {
        await updateApplication(app.id, {
          district: districtRow.districtKey,
          mandal: mandalDraft.trim(),
          village: villageDraft.trim(),
          mobile: {
            ...mobileDraft,
            documentsFromMobile: app.mobile.documentsFromMobile,
            photosFromMobile: cleanedPhotos,
          },
        });
        setMobileDraft((prev) =>
          prev
            ? {
                ...prev,
                photosFromMobile: cleanedPhotos.map((p) => ({ ...p })),
              }
            : prev,
        );
      } catch {
        /* updateApplication shows error toast */
      }
    })();
  }, [app, mobileDraft, districtDraft, mandalDraft, villageDraft, updateApplication, showToast, geoStore]);

  if (!app || !mobileDraft) return null;

  const setField = (key: keyof MobilePayload, value: string) => {
    setMobileDraft((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const previewPhotos = mobileDraft.photosFromMobile.filter((p) => p.url.trim());

  return (
    <>
      <p className="tab-intro">
        Edit beneficiary particulars, bank details, and registration location. Changes save to the console database with the
        rest of the dossier (debounced sync). The mobile document checklist stays read-only. Photos from the mobile app
        appear at the bottom of this tab when sync has stored them.
      </p>

      <div className="card card--gov card--elevated">
        <h2 className="card__title card__title--gov">Registration location</h2>
        <p className="section-lead section-lead--tight text-muted">
          District / mandal / village drive maps, zones, and reports. Pick names from the geo directory when possible.
        </p>
        <div className="table-filters table-filters--geo-row" aria-label="Applicant location">
          <GeoLocationFilters
            idPrefix="ben-tab-geo"
            purpose="applicant"
            district={districtDraft}
            mandal={mandalDraft}
            village={villageDraft}
            onDistrictChange={(d) => {
              setDistrictDraft(d);
              setMandalDraft("");
              setVillageDraft("");
            }}
            onMandalChange={(m) => {
              setMandalDraft(m);
              setVillageDraft("");
            }}
            onVillageChange={setVillageDraft}
          />
        </div>
      </div>

      <div className="card card--gov card--elevated">
        <h2 className="card__title card__title--gov">Beneficiary particulars</h2>
        <div className="field-grid">
          <label className="field">
            <span>Beneficiary name</span>
            <input
              className="form-control"
              value={mobileDraft.beneficiaryName}
              onChange={(e) => setField("beneficiaryName", e.target.value)}
              autoComplete="off"
            />
          </label>
          <label className="field">
            <span>Father’s / husband’s name</span>
            <input
              className="form-control"
              value={mobileDraft.fatherOrHusbandName}
              onChange={(e) => setField("fatherOrHusbandName", e.target.value)}
              autoComplete="off"
            />
          </label>
          <label className="field">
            <span>Aadhaar number</span>
            <input
              className="form-control mono"
              value={mobileDraft.aadhaarNumber}
              onChange={(e) => setField("aadhaarNumber", e.target.value)}
              autoComplete="off"
            />
          </label>
          <label className="field">
            <span>Application ID (mobile)</span>
            <input
              className="form-control mono"
              value={mobileDraft.applicationId}
              onChange={(e) => setField("applicationId", e.target.value)}
              autoComplete="off"
            />
          </label>
          <label className="field">
            <span>House sanction number</span>
            <input
              className="form-control mono"
              value={mobileDraft.houseSanctionNumber}
              onChange={(e) => setField("houseSanctionNumber", e.target.value)}
              autoComplete="off"
            />
          </label>
        </div>
      </div>

      <div className="card card--gov card--elevated">
        <h2 className="card__title card__title--gov">Bank details (as submitted)</h2>
        <div className="field-grid">
          <label className="field">
            <span>Bank name</span>
            <input
              className="form-control"
              value={mobileDraft.bankName}
              onChange={(e) => setField("bankName", e.target.value)}
              autoComplete="off"
            />
          </label>
          <label className="field">
            <span>Bank account number</span>
            <input
              className="form-control mono"
              value={mobileDraft.bankAccountNumber}
              onChange={(e) => setField("bankAccountNumber", e.target.value)}
              autoComplete="off"
            />
          </label>
          <label className="field">
            <span>IFSC</span>
            <input
              className="form-control mono"
              value={mobileDraft.ifscCode}
              onChange={(e) => setField("ifscCode", e.target.value)}
              autoComplete="off"
            />
          </label>
        </div>
      </div>

      <div className="beneficiary-tab-actions">
        <button type="button" className="btn btn--secondary btn--tg" disabled={!dirty} onClick={discard}>
          Discard changes
        </button>
        <button type="button" className="btn btn--primary btn--tg" disabled={!dirty} onClick={save}>
          Save applicant details
        </button>
      </div>

      <div className="card card--gov card--elevated">
        <h2 className="card__title card__title--gov">Photos from mobile</h2>
        {previewPhotos.length === 0 ? (
          <p className="text-muted section-lead section-lead--tight">No photos from mobile yet.</p>
        ) : (
          <div className="mobile-photo-grid" role="list">
            {previewPhotos.map((p, i) => {
              const showImg = isHttpUrlForImage(p.url);
              return (
                <div key={`${p.url}-${i}`} className="mobile-photo-grid__item" role="listitem">
                  <div className="mobile-photo-grid__label">{p.label}</div>
                  {showImg ? (
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="mobile-photo-grid__link">
                      <img src={p.url} alt="" className="mobile-photo-grid__img" loading="lazy" />
                    </a>
                  ) : (
                    <p className="text-muted mobile-photo-grid__no-preview">Image preview is not available for this item.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
